import type { Route, UrlSegment } from '@angular/router';

export type RouteGuardCheck = (route: Route, segments: readonly UrlSegment[]) => Promise<boolean>;

const pathParts = (route: Route): readonly string[] => (route.path ?? '').split('/').filter(Boolean);

const isWarmable = (route: Route): boolean =>
  route.redirectTo === undefined && route.matcher === undefined && route.path !== '**';

const remainingSegments = (route: Route, segments: readonly UrlSegment[]): readonly UrlSegment[] | undefined => {
  const parts = pathParts(route);
  const fits =
    parts.length <= segments.length &&
    parts.every((part, index) => part.startsWith(':') || part === segments[index]?.path);
  const rest = segments.slice(parts.length);

  return fits && !(route.pathMatch === 'full' && rest.length > 0) ? rest : undefined;
};

const isRouteList = (value: unknown): value is readonly Route[] => Array.isArray(value);

const childRoutes = async (route: Route): Promise<readonly Route[]> => {
  const loaded: unknown = await route.loadChildren?.();

  return route.children ?? (isRouteList(loaded) ? loaded : []);
};

const literalPathsFirst = (routes: readonly Route[]): readonly Route[] =>
  routes.toSorted((a, b) => Number(pathParts(a).length === 0) - Number(pathParts(b).length === 0));

export const warmRoute = async (
  routes: readonly Route[],
  segments: readonly UrlSegment[],
  canMatch: RouteGuardCheck
): Promise<boolean> => {
  for (const route of literalPathsFirst(routes.filter((candidate) => isWarmable(candidate)))) {
    const rest = remainingSegments(route, segments);
    if (rest === undefined || !(await canMatch(route, segments))) continue;

    const component = route.loadComponent?.();
    const children = await childRoutes(route);
    const matched = (await warmRoute(children, rest, canMatch)) || rest.length === 0;
    await component;
    if (matched) return true;
  }

  return false;
};
